import { history } from 'instantsearch.js/es/lib/routers';
import {
    getCanonicalFromWindow,
    getDescFromWindow,
    getIndexFromUrlWithEnv,
    getTitleFromWindow,
    getUrlParts,
} from './routing';
import config from '../config/config';

const filterNames = config.FILTER_ATTRIBUTES;

export const routing = ({ onRouteToState }) => ({
    router: history({
        windowTitle(routeState) {
            // console.log( '%cwindowTitle', 'background: Violet; color: white; display: block;', routeState, window.algoliaSearchState );
            // using window.algoliaSearchState because routeState is sometimes the custom object and sometimes the algolia state
            const { index } = window.algoliaSearchState;

            if (!index) {
                return '';
            }

            const metaDescriptionEl = document.querySelector('meta[name="description"]');
            const metaDescription = getDescFromWindow();
            if (!metaDescriptionEl) {
                const meta = document.createElement('meta');
                meta.setAttribute('name', 'description');
                meta.setAttribute('content', metaDescription);
                document.head.appendChild(meta);
            }
            if (metaDescriptionEl) {
                const description = metaDescription;
                metaDescriptionEl.setAttribute('content', description);
            }

            // canonical inclues state, submarket and type (neighborhoods or homes)
            const canonicalEl = document.querySelector('link[rel="canonical"]');
            const canonical = getCanonicalFromWindow();
            if (!canonicalEl) {
                const link = document.createElement('link');
                link.setAttribute('rel', 'canonical');
                link.setAttribute('href', canonical);
                document.head.appendChild(link);
            }
            if (canonicalEl) {
                canonicalEl.setAttribute('href', canonical);
            }

            return getTitleFromWindow();
        },
        createURL({ qsModule, routeState, location }) {
            const pathParts = window.location.pathname.split('/');
            const index = window.algoliaSearchState.index;
            const urlParts = getUrlParts();
            const baseUrl = `${urlParts ? urlParts[1] : ''}`;
            const newUrlSegments = [baseUrl, pathParts[1]];

            // add state
            if (routeState.configure?.filters.includes('state_alpha_2_code')) {
                const state = routeState.configure.filters.match(/state_alpha_2_code:"([^"]+)"/)[1];
                newUrlSegments.push(state.toLowerCase());
            }

            // add submarket
            if (routeState.configure?.filters.includes('submarket_slug')) {
                const submarket = routeState.configure.filters.match(/submarket_slug:"([^"]+)"/)[1];
                // state is not in filters for submarket because Charlotte, nothing shows up - workarond for data issue
                newUrlSegments.push(window.algoliaSearchState.submarket.stateAbbreviation.toLowerCase());
                newUrlSegments.push(submarket);
            }

            const queryParameters = {};

            queryParameters.type = index.includes('home') ? 'home-plans' : 'neighborhoods';

            if (routeState.query) {
                queryParameters.query = routeState.query;
            }

            if (window.algoliaSearchState.page && window.algoliaSearchState.page > 1) {
                queryParameters.page = window.algoliaSearchState.page;
            }

            // if the routeState included the custom object from parseURL, these regex wouldn't be necessary
            // but it's not coming through here
            if (routeState.configure?.filters.includes('cities')) {
                // "state_alpha_2_code:\"sc\" AND (cities:\"Fort Mill\")"
                // 'state_alpha_2_code:"az" AND (cities:"Phoenix" OR cities:"Mesa")';
                const pattern = /cities:"(.*?)"/g;
                const cities = Array.from(routeState.configure?.filters.matchAll(pattern), (match) => match[1]);
                queryParameters.cities = cities
                    .map((city) => {
                        const citySlug = city.replace(/ /g, '-');
                        return citySlug;
                    })
                    .map(encodeURIComponent);
            }

            // square feet
            if (
                routeState.configure?.filters.includes(filterNames.sqFeetMin) ||
                routeState.configure?.filters.includes(filterNames.sqFeetMax)
            ) {
                // this is not a typo, max is checked to be greater than min
                const minMatches = routeState.configure.filters.match(/max_sq_feet > (\d+)/);
                const sqFeetMin = minMatches ? minMatches[1] : '';
                // this is not a typo, min is checked to be less than max
                const maxMatches = routeState.configure.filters.match(/min_sq_feet < (\d+)/);
                const sqFeetMax = maxMatches ? maxMatches[1] : '';
                queryParameters[filterNames.sqFeetInUrl] = `${sqFeetMin}-${sqFeetMax}`;
            }

            // price
            if (
                routeState.configure?.filters.includes(filterNames.priceMin) ||
                routeState.configure?.filters.includes(filterNames.priceMax)
            ) {
                // this is not a typo, max is checked to be greater than min
                const minMatches = routeState.configure.filters.match(/max_price > (\d+)/);
                const priceMin = minMatches ? minMatches[1] : '';
                // this is not a typo, min is checked to be less than max
                const maxMatches = routeState.configure.filters.match(/min_price < (\d+)/);
                const priceMax = maxMatches ? maxMatches[1] : '';
                queryParameters[filterNames.priceInUrl] = `${priceMin}-${priceMax}`;
            }

            // beds
            if (routeState.configure?.filters.includes(filterNames.bedMin)) {
                const bedMin = routeState.configure.filters.match(/min_bedrooms >= (\d+)/)[1];
                queryParameters[filterNames.bedMin] = bedMin;
            }

            if (routeState.configure?.filters.includes(filterNames.bedMax)) {
                const bedMax = routeState.configure.filters.match(/max_bedrooms >= (\d+)/)[1];
                queryParameters[filterNames.bedMax] = bedMax;
            }

            // baths
            if (routeState.configure?.filters.includes(filterNames.bathMin)) {
                const bathMin = routeState.configure.filters.match(/min_bathrooms >= (\d+)/)[1];
                queryParameters[filterNames.bathMin] = bathMin;
            }

            if (routeState.configure?.filters.includes(filterNames.bathMax)) {
                const bathMax = routeState.configure.filters.match(/max_bathrooms >= (\d+)/)[1];
                queryParameters[filterNames.bathMax] = bathMax;
            }

            // garages
            if (routeState.configure?.filters.includes(filterNames.garageMin)) {
                const garageMin = routeState.configure.filters.match(/min_garage >= (\d+)/)[1];
                queryParameters[filterNames.garageMin] = garageMin;
            }

            if (routeState.configure?.filters.includes(filterNames.garageMax)) {
                const garageMax = routeState.configure.filters.match(/max_garage >= (\d+)/)[1];
                queryParameters[config.FILTER_ATTRIBUTES.garageMax] = garageMax;
            }

            if (routeState.refinementList) {
                queryParameters.refinementList = routeState.refinementList;
                // for (const [key, value] of Object.entries(routeState.refinementList)) {
                //     queryParameters.refinementList[key] = {};
                //     for (const [k, v] of Object.entries(value)) {
                //         queryParameters.refinementList[key][k] = v;
                //     }
                // }
            }

            if (routeState.toggle) {
                queryParameters.toggle = {};
                for (const [key, value] of Object.entries(routeState.toggle)) {
                    queryParameters.toggle[key] = value;
                }
            }

            if (routeState.sortBy) {
                queryParameters.sortBy = routeState.sortBy;
            }

            // add search params not in whitelist to the end of the url, e.g. paid search params
            // /find-your-home/ca/riverside-county/?type=neighborhoods&cities=Beaumont&rs=paid_search&medium=adwords_search&cname=IE_Brand_Search&keyword=tri+pointe+homes+beaumont&ds_rl=1236910&gad=1&gclid=Cj0KCQjw1_SkBhDwARIsANbGpFspMHtweM7oxLfeikTGZZ4gJ3yi7f3Ypjo5sDbsNAEeiD2PQ2MlEvsaAimuEALw_wcB&gclsrc=aw.ds
            const currentParams = new URL(location).searchParams;
            for (const [key, value] of currentParams.entries()) {
                if (!(key in config.FILTER_ATTRIBUTES)) {
                    queryParameters[key] = value;
                }
            }

            const queryString = qsModule.stringify(queryParameters, {
                addQueryPrefix: true,
                arrayFormat: 'repeat',
            });

            const url = `${newUrlSegments.join('/')}/${queryString}`;
            // console.log('UPDATE VIA ROUTING:', urlParts, url, queryParameters, queryString);
            return url;
        },
        // also happens on back button
        parseURL({ qsModule, location }) {
            const pathParts = window.location.pathname.split('/');
            const stateSegment = pathParts[2];
            const submarketSegment = pathParts[3];
            const parsed = qsModule.parse(window.location.search.slice(1));
            const index = getIndexFromUrlWithEnv();
            const indexUrlState = parsed[index] ? parsed[index] : parsed;
            // console.log( '%c1. parseURL', 'background: green; color: white; display: block;', { parsed, indexUrlState } );

            const state = {
                ...(indexUrlState?.query && {
                    query: decodeURIComponent(indexUrlState?.query),
                }),
            };

            let minSqFt;
            let maxSqFt;
            if (parsed[filterNames.sqFeetInUrl]) {
                minSqFt = parsed[filterNames.sqFeetInUrl].split('-')[0];
                maxSqFt = parsed[filterNames.sqFeetInUrl].split('-')[1];
            }

            let minPrice;
            let maxPrice;
            if (parsed[filterNames.priceInUrl]) {
                minPrice = parsed[filterNames.priceInUrl].split('-')[0];
                maxPrice = parsed[filterNames.priceInUrl].split('-')[1];
            }

            if (indexUrlState.refinementList) {
                state.refinementList = {};
                for (const [key, value] of Object.entries(indexUrlState.refinementList)) {
                    let decoded = [];
                    if (Array.isArray(value)) {
                        decoded = value.map((v) => decodeURIComponent(v));
                    }
                    if (typeof value === 'string') {
                        decoded = [decodeURIComponent(value)];
                    }
                    state.refinementList[key] = decoded;
                }
            }

            if (indexUrlState.toggle) {
                state.toggle = {};
                for (const [key, value] of Object.entries(indexUrlState.toggle)) {
                    state.toggle[key] = value === 'true' ? true : false;
                }
            }

            if (indexUrlState.sortBy) {
                state.sortBy = indexUrlState.sortBy;
            }

            let cities = [];
            if (parsed.cities) {
                const decodeCity = (city) => {
                    const decoded = decodeURIComponent(city);
                    return decoded.replaceAll('-', ' ');
                };
                cities = Array.isArray(parsed.cities) ? parsed.cities.map(decodeCity) : [decodeCity(parsed.cities)];
            }

            const newState = {
                ...state,
                custom: {
                    index,
                    stateAsLocationAbbr: stateSegment ? stateSegment : null,
                    submarketSlug: submarketSegment ? submarketSegment : null,
                    cities,
                    page: parsed.page ? parseInt(parsed.page) : 1,
                    [filterNames.sqFeetMin]: minSqFt,
                    [filterNames.sqFeetMax]: maxSqFt,
                    [filterNames.bedMin]: parsed[filterNames.bedMin],
                    [filterNames.bedMax]: parsed[filterNames.bedMax],
                    [filterNames.bathMin]: parsed[filterNames.bathMin],
                    [filterNames.bathMax]: parsed[filterNames.bathMax],
                    [filterNames.garageMin]: parsed[filterNames.garageMin],
                    [filterNames.garageMax]: parsed[filterNames.garageMax],
                    [filterNames.priceMin]: minPrice,
                    [filterNames.priceMax]: maxPrice,
                },
            };
            return newState;
        },
    }),
    stateMapping: {
        // Converts uiState to routeState (to URL)
        stateToRoute(uiState) {
            let indexUiState = {};
            for (const key in uiState) {
                if (uiState[key].configure) {
                    indexUiState = uiState[key];
                }
            }

            // just return object without key of index
            return {
                ...indexUiState,
            };
        },

        // Covnerts routeState to uiState (from URL)
        routeToState(routeState) {
            // console.log( '%c2. routeToState', 'background: orange; color: white; display: block;', routeState );
            const index = getIndexFromUrlWithEnv();
            onRouteToState(routeState);

            // return with index key
            return {
                [index]: { ...routeState, custom: routeState.custom },
            };
        },
    },
});
